import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useMemo } from 'react';

interface ChecklistOption {
  key: string;
  displayValue: string;
}

interface SingleSelectChecklistProps {
  options: ChecklistOption[];
  selectedKey: string;
  onSelectionChange: (key: string, option: ChecklistOption) => void;
  hasError?: boolean;
  errorMessage?: string;
  className?: string;
}

export function SingleSelectChecklist({
  options,
  selectedKey,
  onSelectionChange,
  hasError = false,
  errorMessage,
  className,
}: SingleSelectChecklistProps) {
  // Ensure selectedKey is valid - must match one of the option keys or be empty string
  const validSelectedKey = useMemo(() => {
    if (!selectedKey || selectedKey.trim() === '') return '';
    const optionExists = options.some((opt) => opt.key === selectedKey);
    return optionExists ? selectedKey : '';
  }, [selectedKey, options]);

  const handleValueChange = (key: string) => {
    if (!key) return;
    
    const selectedOption = options.find((opt) => opt.key === key);
    if (selectedOption) {
      onSelectionChange(key, selectedOption);
    }
  };

  return (
    <div className={cn('space-y-2', className)}>
      <div
        className={cn(
          'space-y-2',
          hasError && 'border border-destructive rounded-md p-3'
        )}
      >
        <RadioGroup
          value={validSelectedKey ? validSelectedKey : undefined}
          onValueChange={handleValueChange}
          className="space-y-2"
        >
          {options.map((option) => {
            // Create a unique ID for this specific option to avoid conflicts
            const optionId = `checklist-${option.key.replace(/[^a-zA-Z0-9]/g, '-')}`;
            
            return (
              <div key={option.key} className='flex items-start gap-3'>
                <RadioGroupItem
                  value={option.key}
                  id={optionId}
                  className='peer mt-1 flex-shrink-0'
                />
                <Label
                  htmlFor={optionId}
                  className={cn(
                    'cursor-pointer w-full rounded-md border p-3 leading-snug transition-colors',
                    // Default border and hover
                    'border-border hover:bg-accent/50',
                    // When radio is checked (via Radix UI data attribute), apply selected styles
                    'peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5'
                  )}
                >
                  <div className='text-sm font-medium'>
                    {option.displayValue || option.key}
                  </div>
                </Label>
              </div>
            );
          })}
        </RadioGroup>
      </div>

      {hasError && errorMessage && (
        <p className='text-sm text-destructive'>{errorMessage}</p>
      )}
    </div>
  );
}
